<script>
import { authMethods, referralMethods, customerMethods } from '@state/helpers'
import { required, email } from 'vuelidate/lib/validators'
import UserResetPassword from '@components/user-reset-password'
import _ from 'lodash'
import store from '@state/store'

export default {
  components: { UserResetPassword },
  props: {
    fromCheckout: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      email: '',
      password: '',
      authError: null,
      tryingToLogIn: false,
      wasValidated: false,
      messageChange: null,
      checkProducts: false,
      tryingToSendAcctEmail: false,
      resetSuccess: null,
      resetError: null,
    }
  },
  validations: {
    email: {
      required,
      email,
    },
    password: {
      required,
    },
  },
  computed: {
    placeholders() {
      return process.env.NODE_ENV === 'production'
        ? {}
        : {
            email: 'Use "admin@experian.com" to log in with the mock API',
            password: 'Use "password" to log in with the mock API',
          }
    },
    product() {
      // Get the product ID from the store
      const productId = _.get(this, '$store.state.customer.cart.code')
      // Find associated product from list
      return _.get(this, '$store.state.referrer.products').find(
        (product) => product.code === productId
      )
    },
  },
  mounted() {
    this.messageChange = this.$route.query.p
    if (this.messageChange)
      window.history.replaceState(null, null, window.location.pathname)
  },
  methods: {
    ...authMethods,
    ...referralMethods,
    ...customerMethods,
    // Sending the locked account password email
    trySendResetLockedAccountPassword() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.wasValidated = true
      } else {
        this.tryingToSendAcctEmail = true
        return this.updateLockedAccountPassword(this.email)
          .then(() => {
            if (this.$ga) {
              this.$ga.event(
                'event',
                'Links',
                'ForgotPassword',
                'Forgot Password link'
              )
            }
            if (this.$gtag) {
              this.$gtag.event('forgot_password', {})
            }
            this.tryingToSendAcctEmail = false
          })
          .catch((error) => {
            this.tryingToSendAcctEmail = false
            this.resetError = error
          })
      }
    },
    // Try to log the user in with the username
    // and password they provided.
    tryToLogIn() {
      // Reset the authError if it existed.
      this.authError = null
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.wasValidated = true
      } else {
        this.tryingToLogIn = true
        return this.logIn({
          email: this.email,
          password: this.password,
        })
          .then((token) => {
            this.tryingToLogIn = false
            if (this.$gtag) {
              this.$gtag.event('login', {})
            }
            if (!this.fromCheckout) {
              // Redirect to the originally requested page, or to the home page
              this.$router.push(
                this.$route.query.redirectFrom || { name: 'dashboard' }
              )
            } else if (!this.product) {
              this.$router.push(
                // Redirect to home page if logging in from checkout and no product is in cart
                { path: '/vehiclehistory/' }
              )
            }
            if (this.fromCheckout) {
              this.checkProducts = true
            }
          })
          .then((checks) => {
            if (this.checkProducts) {
              return this.getProducts().then((response) => {
                if (!this.product) {
                  // sets flag saying bad product in cart
                  this.setBadCart(true)
                  this.tryingToLogIn = false
                  // Clear cart and redirect to product page
                  store.dispatch('customer/clearCart')
                  this.$router.push(
                    this.$route.query.redirectFrom || { name: 'reports-list' }
                  )
                }
              })
            }
          })
          .catch((error) => {
            this.tryingToLogIn = false
            // check for locked account and display alert while login
            let errorMessage = error.response.data.message
            if (
              errorMessage ===
              'Account is locked after 5 unsuccessful attempts. Password reset email will be sent.'
            ) {
              errorMessage =
                'Your account has been locked after max attempts for security reasons. Please check your email for unlocking account.'
              this.trySendResetLockedAccountPassword()
            } else {
              errorMessage =
                "We couldn't find your account: Please check your email address or password and try again."
            }
            this.authError = errorMessage
          })
      }
    },
  },
}
</script>

<template>
  <div>
    <form
      :class="{ 'was-validated ': wasValidated }"
      class="needs-validation"
      novalidate
      @submit.prevent="tryToLogIn"
    >
      <b-alert v-if="authError" variant="danger" show>
        {{ authError }}
      </b-alert>
      <b-alert v-if="messageChange == 2" variant="danger" show>
        Your previous session has expired. For security purposes, you must login
        again to continue.
      </b-alert>
      <b-alert v-if="messageChange == 1" variant="success" show>
        Your preference has been updated. For security purposes, you must login
        with your updated profile to continue.
      </b-alert>
      <b-alert v-if="resetSuccess" variant="success" show dismissible>
        {{ resetSuccess }}
      </b-alert>
      <b-alert v-if="resetError" variant="danger" show dismissible>
        {{ resetError }}
      </b-alert>
      <BaseInput
        v-model.trim="$v.email.$model"
        name="email"
        label="E-mail"
        :placeholder="placeholders.email"
        :has-error="$v.email.$error && wasValidated"
      >
        <div v-if="!$v.email.required" class="invalid-feedback"
          >Field is required</div
        >
        <div v-if="!$v.email.email" class="invalid-feedback"
          >Please enter a valid email address</div
        >
      </BaseInput>

      <BaseInput
        v-model="$v.password.$model"
        name="password"
        type="password"
        label="Password"
        :placeholder="placeholders.password"
        :has-error="$v.password.$error && wasValidated"
      >
        <div v-if="!$v.password.required" class="invalid-feedback"
          >Field is required</div
        >
      </BaseInput>
      <BaseButton :disabled="tryingToLogIn" type="submit">
        <BaseIcon v-if="tryingToLogIn" name="sync" spin />
        <span v-else>
          {{ fromCheckout ? 'Continue' : 'Sign in' }}
        </span>
      </BaseButton>
    </form>
    <UserResetPassword />
  </div>
</template>

<style lang="scss" module>
@import '@design';
</style>
